/* Noemi Flowers — lightweight carousels / sliders / tabs used by nf-* sections.
   <nf-carousel>   scroll-snap track with arrows, progress bar, dots, optional centered "coverflow" mode
   <nf-hero-slider> fading hero slides with dots + autoplay
   <nf-tabs>        tabs with prev/next arrows */

if (!customElements.get('nf-carousel')) {
  class NfCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;
      this.items = Array.from(this.track.children).filter((el) => !el.hasAttribute('data-ignore'));
      this.prevButtons = this.querySelectorAll('[data-prev]');
      this.nextButtons = this.querySelectorAll('[data-next]');
      this.fill = this.querySelector('[data-progress-fill]');
      this.dotsWrap = this.querySelector('[data-dots]');
      this.centered = this.hasAttribute('data-center');
      this.activeIndex = -1;

      this.prevButtons.forEach((b) => b.addEventListener('click', () => this.go(-1)));
      this.nextButtons.forEach((b) => b.addEventListener('click', () => this.go(1)));

      if (this.dotsWrap) this.buildDots();

      this.onScroll = this.onScroll.bind(this);
      this.track.addEventListener('scroll', () => {
        if (this.raf) return;
        this.raf = requestAnimationFrame(() => {
          this.raf = null;
          this.onScroll();
        });
      }, { passive: true });

      this.resizeObserver = new ResizeObserver(() => this.onScroll());
      this.resizeObserver.observe(this.track);

      this.querySelectorAll('[data-mute-toggle]').forEach((btn) =>
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.muted = !this.muted;
          this.querySelectorAll('video').forEach((v) => (v.muted = this.muted));
          this.querySelectorAll('[data-mute-toggle]').forEach((b) => {
            b.setAttribute('aria-pressed', String(!this.muted));
            b.classList.toggle('is-unmuted', !this.muted);
          });
        })
      );
      this.muted = true;

      if (this.centered) {
        this.items.forEach((item, i) =>
          item.addEventListener('click', (e) => {
            if (i === this.activeIndex) return;
            if (e.target.closest('[data-mute-toggle]')) return;
            e.preventDefault();
            this.scrollToIndex(i);
          })
        );
        const start = Math.min(parseInt(this.dataset.startIndex || '0', 10), this.items.length - 1);
        requestAnimationFrame(() => {
          this.scrollToIndex(start, 'instant');
          this.onScroll();
        });
      } else {
        this.onScroll();
      }

      if ('IntersectionObserver' in window && this.centered) {
        this.visible = false;
        new IntersectionObserver((entries) => {
          this.visible = entries[0].isIntersecting;
          this.syncVideos();
        }, { threshold: 0.25 }).observe(this);
      }
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    step() {
      if (this.items.length < 2) return this.track.clientWidth;
      return this.items[1].offsetLeft - this.items[0].offsetLeft;
    }

    go(dir) {
      if (this.centered) {
        const target = Math.max(0, Math.min(this.items.length - 1, this.currentCenterIndex() + dir));
        this.scrollToIndex(target);
        return;
      }
      this.track.scrollBy({ left: dir * this.step(), behavior: 'smooth' });
    }

    scrollToIndex(index, behavior = 'smooth') {
      const item = this.items[index];
      if (!item) return;
      let left;
      if (this.centered) {
        left = item.offsetLeft + item.offsetWidth / 2 - this.track.clientWidth / 2;
      } else {
        left = item.offsetLeft - this.items[0].offsetLeft;
      }
      this.track.scrollTo({ left, behavior });
    }

    currentCenterIndex() {
      const center = this.track.scrollLeft + this.track.clientWidth / 2;
      let best = 0;
      let bestDist = Infinity;
      this.items.forEach((item, i) => {
        const d = Math.abs(item.offsetLeft + item.offsetWidth / 2 - center);
        if (d < bestDist) {
          bestDist = d;
          best = i;
        }
      });
      return best;
    }

    currentStartIndex() {
      const s = this.step() || 1;
      return Math.round(this.track.scrollLeft / s);
    }

    buildDots() {
      this.dotsWrap.innerHTML = '';
      this.dots = this.items.map((_, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'nf-dots__dot';
        b.setAttribute('aria-label', `${this.dataset.dotLabel || 'Ir a'} ${i + 1}`);
        b.addEventListener('click', () => this.scrollToIndex(i));
        this.dotsWrap.appendChild(b);
        return b;
      });
    }

    onScroll() {
      const t = this.track;
      const max = t.scrollWidth - t.clientWidth;
      const atStart = t.scrollLeft <= 2;
      const atEnd = t.scrollLeft >= max - 2;
      this.classList.toggle('is-scrollable', max > 2);

      if (this.fill) {
        const ratio = max > 0 ? (t.scrollLeft + t.clientWidth) / t.scrollWidth : 1;
        this.fill.style.width = `${Math.min(100, ratio * 100)}%`;
      }

      const index = this.centered ? this.currentCenterIndex() : this.currentStartIndex();
      if (!this.centered) {
        this.prevButtons.forEach((b) => (b.disabled = atStart));
        this.nextButtons.forEach((b) => (b.disabled = atEnd));
      } else {
        this.prevButtons.forEach((b) => (b.disabled = index === 0));
        this.nextButtons.forEach((b) => (b.disabled = index === this.items.length - 1));
      }

      if (index !== this.activeIndex) {
        this.activeIndex = index;
        if (this.dots) {
          // When the track ends, highlight the last dot.
          const dotIndex = !this.centered && atEnd && max > 2 ? this.items.length - 1 : index;
          this.dots.forEach((d, i) => d.classList.toggle('is-active', i === dotIndex));
        }
        if (this.centered) {
          this.items.forEach((item, i) => {
            item.classList.toggle('is-active', i === index);
            item.classList.toggle('is-before', i < index);
            item.classList.toggle('is-after', i > index);
          });
          this.syncVideos();
        }
      } else if (this.dots && !this.centered && atEnd && max > 2) {
        this.dots.forEach((d, i) => d.classList.toggle('is-active', i === this.items.length - 1));
      }
    }

    syncVideos() {
      this.items.forEach((item, i) => {
        const video = item.querySelector('video');
        if (!video) return;
        if (i === this.activeIndex && this.visible !== false) {
          video.muted = this.muted;
          const p = video.play();
          if (p && p.catch) p.catch(() => {});
        } else {
          video.pause();
        }
      });
    }
  }
  customElements.define('nf-carousel', NfCarousel);
}

if (!customElements.get('nf-hero-slider')) {
  class NfHeroSlider extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-dot]'));
      this.index = 0;
      this.dots.forEach((d, i) =>
        d.addEventListener('click', () => {
          this.show(i);
          this.restart();
        })
      );
      this.interval = parseInt(this.dataset.autoplay || '0', 10) * 1000;
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.restart());
      this.addEventListener('focusin', () => this.stop());
      this.restart();

      if (window.Shopify && Shopify.designMode) {
        document.addEventListener('shopify:block:select', (e) => {
          const i = this.slides.findIndex((s) => s.contains(e.target) || s === e.target);
          if (i > -1) {
            this.stop();
            this.show(i);
          }
        });
      }
    }
    show(i) {
      this.index = (i + this.slides.length) % this.slides.length;
      this.slides.forEach((s, n) => {
        const active = n === this.index;
        s.classList.toggle('is-active', active);
        s.setAttribute('aria-hidden', String(!active));
        s.querySelectorAll('a, button').forEach((el) => (active ? el.removeAttribute('tabindex') : el.setAttribute('tabindex', '-1')));
      });
      this.dots.forEach((d, n) => {
        d.classList.toggle('is-active', n === this.index);
        d.setAttribute('aria-current', String(n === this.index));
      });
    }
    stop() {
      clearInterval(this.timer);
    }
    restart() {
      this.stop();
      if (this.interval > 0 && this.slides.length > 1) {
        this.timer = setInterval(() => this.show(this.index + 1), this.interval);
      }
    }
  }
  customElements.define('nf-hero-slider', NfHeroSlider);
}

if (!customElements.get('nf-tabs')) {
  class NfTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
            e.preventDefault();
            const n = this.current + (e.key === 'ArrowRight' ? 1 : -1);
            this.select(n, true);
          }
        });
      });
      this.querySelectorAll('[data-tab-prev]').forEach((b) => b.addEventListener('click', () => this.select(this.current - 1)));
      this.querySelectorAll('[data-tab-next]').forEach((b) => b.addEventListener('click', () => this.select(this.current + 1)));
      this.current = Math.max(0, this.tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true'));

      if (window.Shopify && Shopify.designMode) {
        document.addEventListener('shopify:block:select', (e) => {
          const i = this.panels.findIndex((p) => p.contains(e.target) || p === e.target);
          if (i > -1) this.select(i);
        });
      }
    }
    select(i, focus = false) {
      const n = (i + this.tabs.length) % this.tabs.length;
      this.current = n;
      this.tabs.forEach((t, k) => {
        const on = k === n;
        t.setAttribute('aria-selected', String(on));
        t.tabIndex = on ? 0 : -1;
      });
      this.panels.forEach((p, k) => (p.hidden = k !== n));
      if (focus) this.tabs[n].focus();
      const activeTab = this.tabs[n];
      const list = activeTab.parentElement;
      if (list && list.scrollWidth > list.clientWidth) {
        list.scrollTo({ left: activeTab.offsetLeft - list.offsetLeft - 16, behavior: 'smooth' });
      }
    }
  }
  customElements.define('nf-tabs', NfTabs);
}
